/**
 * DriveContent — main content area for the Drive file manager.
 *
 * Reads folders, files, loading and error signals from DriveStoreContext.
 * Renders a Bootstrap table. When inside a subfolder, the first row is a
 * ".." parent-folder navigation row (Windows Explorer style).
 * Column headers sort via store.setSort (server-side + full list reload).
 * Infinite scroll: IntersectionObserver sentinel calls store.loadMore().
 * Breadcrumb navigation is handled by drive_controller.js via an effect() that
 * updates the layout header breadcrumb DOM reactively.
 *
 * Reactivity: uses useState + useEffect with @preact/signals-core effect()
 * to bridge signals → Preact state, ensuring re-renders when signal values change.
 */

import { h } from 'preact'
import { useContext, useState, useEffect, useRef } from 'preact/hooks'
import { effect } from '@preact/signals-core'
import { DriveStoreContext, DriveI18nContext } from './drive-context.js'
import { FolderRow } from './FolderRow.jsx'
import { FileRow } from './FileRow.jsx'

function ParentFolderRow({ parentCrumb }) {
  const store = useContext(DriveStoreContext)

  function handleClick(e) {
    e.preventDefault()
    store.loadFolder(parentCrumb.path + '.json')
    history.pushState({}, '', parentCrumb.path)
  }

  function handleKeyDown(e) {
    // Enter is also handled by drive_controller; keep local fallback for focus inside row
    if (e.key === 'Enter') {
      e.preventDefault()
      handleClick(e)
    }
  }

  return (
    <tr
      style="cursor: pointer;"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      class="text-muted"
      tabindex="0"
      data-item-type="back"
      data-item-id="parent"
    >
      <td class="text-center align-middle">
        <i class="bi bi-arrow-up-circle text-secondary"></i>
      </td>
      <td class="align-middle" colSpan={4}>
        <span class="text-secondary fw-medium">..</span>
        <small class="ms-2 text-muted">{parentCrumb.name}</small>
      </td>
      <td></td>
    </tr>
  )
}

function SortableTh({ column, label, sortColumn, sortDir, onSort, className = '' }) {
  const active = sortColumn === column
  const ariaSort = active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
  const icon = !active
    ? 'bi-arrow-down-up'
    : sortDir === 'asc'
      ? 'bi-sort-up'
      : 'bi-sort-down'

  return (
    <th
      class={`drive-sort-th ${className}`.trim()}
      aria-sort={ariaSort}
      scope="col"
    >
      <button
        type="button"
        class="drive-sort-btn"
        onClick={() => onSort(column)}
        aria-pressed={active ? 'true' : 'false'}
      >
        <span>{label}</span>
        <i class={`bi ${icon}`} aria-hidden="true"></i>
      </button>
    </th>
  )
}

function LoadMoreSentinel({ hasMore, loadingMore, onVisible }) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el || !hasMore) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some(entry => entry.isIntersecting)) onVisible()
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, onVisible])

  if (!hasMore && !loadingMore) return null

  return (
    <div ref={ref} class="text-center py-3 text-muted" aria-hidden={!loadingMore}>
      {loadingMore && (
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">Loading</span>
        </div>
      )}
    </div>
  )
}

export function DriveContent() {
  const store = useContext(DriveStoreContext)
  const i18n  = useContext(DriveI18nContext)
  const t     = (key) => i18n ? i18n.t(key) : key

  const [state, setState] = useState({
    folders:       store.folders.value,
    files:         store.files.value,
    loading:       store.loading.value,
    loadingMore:   store.loadingMore.value,
    hasMore:       store.hasMore.value,
    error:         store.error.value,
    selectedIds:   store.selectedIds.value,
    currentFolder: store.currentFolder.value,
    breadcrumbs:   store.breadcrumbs.value,
    sortColumn:    store.sortColumn.value,
    sortDir:       store.sortDir.value,
  })

  useEffect(() => {
    const dispose = effect(() => {
      const folders       = store.folders.value
      const files         = store.files.value
      const loading       = store.loading.value
      const loadingMore   = store.loadingMore.value
      const hasMore       = store.hasMore.value
      const error         = store.error.value
      const selectedIds   = store.selectedIds.value
      const currentFolder = store.currentFolder.value
      const breadcrumbs   = store.breadcrumbs.value
      const sortColumn    = store.sortColumn.value
      const sortDir       = store.sortDir.value
      setState({
        folders, files, loading, loadingMore, hasMore, error, selectedIds,
        currentFolder, breadcrumbs, sortColumn, sortDir,
      })
    })
    return dispose
  }, [store])

  const {
    folders: folderList, files: fileList,
    loading: isLoading, loadingMore, hasMore, error: errorMsg,
    selectedIds, currentFolder, breadcrumbs,
    sortColumn, sortDir,
  } = state

  // Show ".." row when inside a subfolder (currentFolder is set and there's a parent crumb)
  const parentCrumb = currentFolder && breadcrumbs.length >= 2
    ? breadcrumbs[breadcrumbs.length - 2]
    : null

  if (isLoading) {
    return (
      <div class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{t('drive.uploading')}</span>
        </div>
      </div>
    )
  }

  if (errorMsg) {
    return (
      <div class="alert alert-danger" role="alert">
        <i class="bi bi-exclamation-triangle me-2"></i>
        {errorMsg}
      </div>
    )
  }

  if (!parentCrumb && folderList.length === 0 && fileList.length === 0) {
    return (
      <div class="text-center py-5 text-muted">
        <i class="bi bi-folder-x display-4 d-block mb-3"></i>
        <p class="mb-0">{t('drive.empty_folder_content')}</p>
      </div>
    )
  }

  return (
    <div class="table-responsive">
      <table class="table table-hover table-sm mb-0">
        <thead class="table-light">
          <tr>
            <th style="width: 2.5rem;"></th>
            <SortableTh
              column="name"
              label={t('drive.col_name')}
              sortColumn={sortColumn}
              sortDir={sortDir}
              onSort={store.setSort}
            />
            <th class="d-none d-md-table-cell">{t('drive.uploaded_by')}</th>
            <SortableTh
              column="size"
              label={t('drive.col_size')}
              sortColumn={sortColumn}
              sortDir={sortDir}
              onSort={store.setSort}
              className="d-none d-md-table-cell"
            />
            <SortableTh
              column="created"
              label={t('drive.col_created')}
              sortColumn={sortColumn}
              sortDir={sortDir}
              onSort={store.setSort}
              className="d-none d-md-table-cell"
            />
            <th class="drive-actions-th"></th>
          </tr>
        </thead>

        <tbody data-section="folders">
          {parentCrumb && <ParentFolderRow parentCrumb={parentCrumb} />}
          {folderList.map(folder => (
            <FolderRow key={folder.id} folder={folder} selectedIds={selectedIds} />
          ))}
        </tbody>

        {fileList.length > 0 && (
          <tbody data-section="files">
            {fileList.map(file => (
              <FileRow key={file.id} file={file} selectedIds={selectedIds} />
            ))}
          </tbody>
        )}
      </table>

      <LoadMoreSentinel
        hasMore={hasMore}
        loadingMore={loadingMore}
        onVisible={store.loadMore}
      />
    </div>
  )
}
